import { useEffect, useState } from "react";
import { LayoutGroup, motion, useReducedMotion } from "framer-motion";
import Images from "../utils/Images";

const EASE = [0.22, 1, 0.36, 1];

const JOURNEY = ["Learn", "Compare", "Verify", "Recover", "Grow"];

const JOURNEY_BEATS = [
  {
    title: "Searching for the right financial product",
    hint: "SimMarket",
    href: "#simmarket",
    visual: "market",
  },
  {
    title: "Verifying borrowers and assets",
    hint: "SimVerify",
    href: "/simverify",
    visual: "verify",
  },
  {
    title: "Improving debt recovery performance",
    hint: "SimCollect",
    href: "/simcollect",
    visual: "collect",
  },
];

const PILLARS = ["Intelligence", "Trust", "Technology"];

const SOLUTIONS = [
  { name: "SimMarket", href: "#simmarket", line: "Marketplace & acquisition" },
  {
    name: "SimVerify",
    href: "/simverify",
    line: "Digital trust & verification",
  },
  { name: "SimCollect", href: "/simcollect", line: "Intelligent collections" },
];

function Hero() {
  const reduce = useReducedMotion();
  const [activeStep, setActiveStep] = useState(0);
  const enter = reduce ? { duration: 0 } : { duration: 0.55, ease: EASE };
  const fadeUp = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: enter,
    },
  };

  useEffect(() => {
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setActiveStep((step) => (step + 1) % JOURNEY.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, [reduce]);

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="hero-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_100%_0%,rgba(229,184,11,0.14),transparent_55%),radial-gradient(80%_60%_at_0%_100%,rgba(12,31,27,0.05),transparent_50%),linear-gradient(180deg,#ffffff_0%,#f7faf8_48%,#ffffff_100%)]" />
        <div
          className="absolute inset-0 opacity-[0.45]"
          style={{
            backgroundImage:
              "radial-gradient(rgba(12,31,27,0.09) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(12,31,27,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(12,31,27,0.06) 1px, transparent 1px)",
            backgroundSize: "88px 88px",
          }}
        />
        <div className="absolute -left-24 top-28 h-[28rem] w-[28rem] rounded-full border border-navy/[0.06]" />
        <div className="absolute -left-8 top-44 h-[18rem] w-[18rem] rounded-full border border-gold/20" />
        <div className="absolute -right-16 bottom-10 h-[22rem] w-[22rem] rounded-full border border-navy/[0.05]" />
        <p className="absolute -left-6 top-8 select-none whitespace-nowrap text-[9rem] font-black leading-none tracking-tight text-navy/[0.035] sm:text-[13rem] lg:top-2 lg:text-[16rem]">
          ECOSYSTEM
        </p>
      </div>
      <div
        className="pointer-events-none absolute inset-0 z-[2]"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-[linear-gradient(105deg,rgba(255,255,255,0.72)_0%,rgba(52,132,110,0.14)_46%,rgba(12,31,27,0.32)_100%)]" />
        <div className="absolute inset-y-0 right-0 w-[62%] bg-[radial-gradient(70%_60%_at_78%_42%,rgba(52,132,110,0.28),transparent_68%)]" />
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[3] h-px bg-gradient-to-r from-transparent via-gold/70 to-transparent" />

      <div className="relative mx-auto grid max-w-8xl items-start gap-8 px-4 pb-12 pt-[5.5rem] sm:px-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-stretch lg:gap-2 lg:px-8 lg:pb-16 lg:pt-[5.5rem]">
        <motion.div
          className="relative z-[3] flex h-full min-w-0 flex-col pt-6 [container-type:inline-size] lg:pt-10"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: reduce ? 0 : 0.09,
                delayChildren: reduce ? 0 : 0.06,
              },
            },
          }}
        >
          <motion.h1
            id="hero-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(1.9rem,8.8cqi,4.35rem)]"
            variants={fadeUp}
          >
            <span className="block sm:whitespace-nowrap">
              Africa&apos;s intelligent digital
            </span>
            <span className="block sm:whitespace-nowrap">
              financial ecosystem
            </span>
          </motion.h1>

          <motion.div variants={fadeUp}>
            <JourneyTrack
              activeStep={activeStep}
              reduce={reduce}
              onSelect={setActiveStep}
            />
          </motion.div>

          <motion.p
            className="mt-6 max-w-3xl font-medium leading-snug text-navy/70 [font-size:clamp(1.05rem,3.4cqi,1.35rem)]"
            variants={fadeUp}
          >
            One ecosystem. Three intelligent solutions. Endless financial
            opportunities.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 max-w-[42rem]">
            <p className="leading-[1.8] text-navy/75 [font-size:clamp(1rem,2.9cqi,1.125rem)] sm:leading-[1.85]">
              SimFinance is Africa&apos;s intelligent digital financial
              ecosystem that connects{" "}
              <span className="font-semibold text-navy">consumers</span>
              <span className="text-navy/25"> · </span>
              <span className="font-semibold text-navy">
                financial institutions
              </span>
              <span className="text-navy/25"> · </span>
              <span className="font-semibold text-navy">technology</span>{" "}
              through one integrated platform.
            </p>

            <div className="my-5 flex items-center gap-3" aria-hidden="true">
              <span className="h-[2px] w-10 rounded-full bg-gold" />
              <span className="h-px flex-1 max-w-[7rem] bg-navy/10" />
            </div>

            <JourneyCard />
          </motion.div>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-3"
            variants={fadeUp}
          >
            <a
              href="#simmarket"
              className="inline-flex min-h-12 cursor-pointer items-center rounded-full bg-gold px-7 text-base font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
            >
              Explore the suite
            </a>
            <a
              href="#contact"
              className="inline-flex min-h-12 cursor-pointer items-center rounded-full border border-navy/15 px-7 text-base font-semibold text-navy transition hover:border-navy hover:bg-navy hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
            >
              Talk to an advisor
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          className="relative z-0 flex h-full min-w-0 flex-col justify-end lg:-ml-8 lg:w-[120%] lg:max-w-none"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...enter, delay: reduce ? 0 : 0.12 }}
        >
          <HeroArt reduce={reduce} />
        </motion.div>
      </div>

      <div className="relative z-[3] border-t border-navy/10 bg-white/70 backdrop-blur-[2px]">
        <span className="absolute left-1/2 top-0 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-navy/10 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/45 shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
          The SimFinance suite
        </span>
        <div className="mx-auto grid max-w-8xl sm:grid-cols-3">
          {SOLUTIONS.map((item, index) => (
            <a
              key={item.name}
              href={item.href}
              className={`group flex min-h-[5.5rem] cursor-pointer items-center justify-between px-4 py-5 transition hover:bg-navy/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:px-8 ${
                index > 0 ? "sm:border-l sm:border-navy/10" : ""
              }`}
            >
              <span>
                <span className="block text-sm font-semibold text-navy">
                  {item.name}
                </span>
                <span className="mt-1 block text-sm text-navy/50">
                  {item.line}
                </span>
              </span>
              <svg
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
                className="h-4 w-4 text-gold transition group-hover:translate-x-0.5"
              >
                <path
                  d="M3 8H13M13 8L9 4M13 8L9 12"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function JourneyCard() {
  return (
    <article className="relative overflow-hidden rounded-2xl border border-navy/10 bg-white/85 shadow-[0_18px_50px_rgba(12,31,27,0.08)] backdrop-blur-[2px]">
      <div className="h-[3px] w-full bg-gradient-to-r from-gold via-royal to-navy" />

      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        aria-hidden="true"
        style={{
          backgroundImage:
            "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      />

      <div className="relative px-5 pb-2 pt-5 sm:px-6 sm:pt-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/40">
          Whether you&apos;re
        </p>

        <ul className="mt-4">
          {JOURNEY_BEATS.map((beat, index) => (
            <li key={beat.title}>
              <a
                href={beat.href}
                className="group flex items-center gap-3.5 py-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy text-gold shadow-[0_8px_20px_rgba(12,31,27,0.18)]">
                  <BeatIcon type={beat.visual} />
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-gold text-[9px] font-bold text-navy">
                    {index + 1}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-semibold tracking-[0.2em] text-navy/35">
                    {beat.hint}
                  </span>
                  <span className="mt-0.5 block text-[0.95rem] font-medium leading-snug text-navy transition-colors group-hover:text-royal sm:text-base">
                    {beat.title}
                  </span>
                </span>
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-gold transition group-hover:translate-x-0.5"
                >
                  <path
                    d="M3 8H13M13 8L9 4M13 8L9 12"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              {index < JOURNEY_BEATS.length - 1 ? (
                <div className="ml-[3.6rem] h-px bg-navy/[0.08]" />
              ) : null}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative mt-1 bg-navy px-5 py-4 text-white sm:px-6">
        <div className="pointer-events-none absolute -right-6 top-0 h-20 w-20 rounded-full bg-royal/30 blur-2xl" />
        <p className="relative text-[11px] font-semibold uppercase tracking-[0.22em] text-gold/90">
          SimFinance provides
        </p>
        <div className="relative mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          {PILLARS.map((pillar, index) => (
            <span key={pillar} className="inline-flex items-center gap-2">
              <span className="text-sm font-semibold tracking-tight text-white sm:text-[0.95rem]">
                {pillar}
              </span>
              {index < PILLARS.length - 1 ? (
                <span className="text-gold/80" aria-hidden="true">
                  ·
                </span>
              ) : null}
            </span>
          ))}
        </div>
        <p className="relative mt-2 text-sm leading-relaxed text-white/60">
          to power every stage of the financial customer journey.
        </p>
      </div>
    </article>
  );
}

function BeatIcon({ type }) {
  if (type === "verify") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="h-5 w-5"
      >
        <path
          d="M12 3.5L5.5 6.2V11.4C5.5 15.4 8.2 18.9 12 20.5C15.8 18.9 18.5 15.4 18.5 11.4V6.2L12 3.5Z"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <path
          d="M9.2 12.1L11.1 14L15 9.8"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "collect") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="h-5 w-5"
      >
        <path
          d="M5 16.5L10 11.5L13.5 15L19 8.5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.5 8.5H19V13"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
      <circle cx="11" cy="11" r="6.2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M15.8 15.8L19.5 19.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function JourneyTrack({ activeStep, reduce, onSelect }) {
  const last = JOURNEY.length - 1;
  const spring = reduce
    ? { duration: 0 }
    : { type: "spring", stiffness: 340, damping: 32, mass: 0.6 };

  return (
    <div className="mt-8" aria-label="Financial journey">
      <LayoutGroup id="hero-journey">
        <ol className="flex min-w-0 max-w-full flex-nowrap items-end">
          {JOURNEY.map((word, index) => {
            const active = index === activeStep;
            const passed = index < activeStep;

            return (
              <li key={word} className="flex items-end">
                <button
                  type="button"
                  className="group relative cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4"
                  aria-current={active ? "step" : undefined}
                  onClick={() => onSelect(index)}
                >
                  {active ? (
                    <motion.span
                      layoutId="journey-glow"
                      className="pointer-events-none absolute -inset-x-3 -inset-y-2 rounded-full bg-gold/15 blur-2xl"
                      transition={spring}
                    />
                  ) : null}

                  <span className="relative flex items-center gap-2">
                    <span className="relative inline-flex h-1.5 w-1.5 shrink-0 items-center justify-center">
                      {active && !reduce ? (
                        <motion.span
                          className="absolute inset-0 rounded-full bg-gold"
                          animate={{
                            scale: [1, 2.6, 1],
                            opacity: [0.55, 0, 0.55],
                          }}
                          transition={{
                            duration: 1.7,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      ) : null}
                      <span
                        className={`relative z-[1] h-1.5 w-1.5 rounded-full ${
                          active
                            ? "bg-gold"
                            : passed
                              ? "bg-royal"
                              : "bg-navy/20"
                        }`}
                      />
                    </span>
                    <span
                      className={`text-[10px] font-semibold tracking-[0.28em] sm:text-[11px] ${
                        active
                          ? "text-gold"
                          : passed
                            ? "text-royal/70"
                            : "text-navy/28"
                      }`}
                    >
                      0{index + 1}
                    </span>
                  </span>

                  <span className="relative mt-1.5 inline-block">
                    <motion.span
                      className="relative inline-block whitespace-nowrap font-semibold tracking-tight [font-size:clamp(1.15rem,4.4cqi,2.05rem)]"
                      animate={{
                        color: active
                          ? "#34846E"
                          : passed
                            ? "rgba(12,31,27,0.55)"
                            : "rgba(12,31,27,0.24)",
                      }}
                      transition={spring}
                    >
                      {word}
                    </motion.span>
                    {active ? (
                      <motion.span
                        layoutId="journey-underline"
                        className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full bg-gold"
                        transition={spring}
                      />
                    ) : null}
                  </span>
                </button>

                {index < last ? (
                  <span
                    className="relative mb-[0.55rem] block h-[2px] overflow-hidden [margin-inline:clamp(0.35rem,1.2cqi,1.25rem)] [width:clamp(0.6rem,2.3cqi,3rem)]"
                    aria-hidden="true"
                  >
                    <span className="absolute inset-0 bg-navy/[0.1]" />
                    <motion.span
                      className="absolute inset-0 origin-left bg-gradient-to-r from-gold to-royal"
                      animate={{ scaleX: passed ? 1 : 0 }}
                      transition={spring}
                    />
                    {active && !reduce ? (
                      <motion.span
                        className="absolute inset-y-0 w-8 bg-gradient-to-r from-transparent via-white to-transparent opacity-80"
                        animate={{ x: ["-2rem", "3rem"] }}
                        transition={{
                          duration: 1.4,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />
                    ) : null}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      </LayoutGroup>
    </div>
  );
}

function HeroArt({ reduce }) {
  const [artFailed, setArtFailed] = useState(false);

  if (artFailed) {
    return <EcosystemFallback />;
  }

  return (
    <div className="relative mx-auto flex h-full w-full max-w-xl items-end lg:mx-0 lg:max-w-none">
      <img
        src={Images.hero}
        alt="SimFinance connecting consumers, institutions and technology across Africa"
        className="h-auto w-full lg:h-full lg:object-contain lg:object-bottom lg:object-right"
        onError={() => setArtFailed(true)}
      />
      <motion.div
        className="pointer-events-none absolute -left-6 top-[18%] hidden h-24 w-24 rounded-full border border-gold/25 lg:block"
        animate={reduce ? undefined : { y: [0, -8, 0] }}
        transition={
          reduce
            ? undefined
            : { duration: 5.5, repeat: Infinity, ease: "easeInOut" }
        }
        aria-hidden="true"
      />
      <motion.div
        className="pointer-events-none absolute -right-4 bottom-[22%] hidden h-16 w-16 rounded-full border border-navy/10 lg:block"
        animate={reduce ? undefined : { y: [0, 10, 0] }}
        transition={
          reduce
            ? undefined
            : { duration: 6.2, repeat: Infinity, ease: "easeInOut" }
        }
        aria-hidden="true"
      />
    </div>
  );
}

function EcosystemFallback() {
  return (
    <div className="flex flex-col justify-between">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
          One platform
        </p>
        <p className="mt-3 max-w-xs text-2xl font-semibold leading-tight tracking-tight text-royal">
          Intelligence across the financial customer journey.
        </p>
      </div>

      <svg
        viewBox="0 0 360 220"
        className="mx-auto w-full max-w-md"
        aria-hidden="true"
      >
        <circle
          cx="180"
          cy="110"
          r="88"
          fill="none"
          stroke="rgba(12,31,27,0.12)"
          strokeWidth="1"
        />
        <circle
          cx="180"
          cy="110"
          r="52"
          fill="none"
          stroke="rgba(229,184,11,0.55)"
          strokeWidth="1"
        />
        <line x1="180" y1="38" x2="180" y2="78" stroke="rgba(12,31,27,0.2)" />
        <line x1="102" y1="150" x2="148" y2="128" stroke="rgba(12,31,27,0.2)" />
        <line x1="258" y1="150" x2="212" y2="128" stroke="rgba(12,31,27,0.2)" />

        <circle cx="180" cy="110" r="28" fill="#0c1f1b" />
        <text
          x="180"
          y="114"
          textAnchor="middle"
          fill="#ffffff"
          fontSize="9"
          fontFamily="Onest, sans-serif"
          fontWeight="600"
        >
          SimFinance
        </text>

        <circle cx="180" cy="28" r="18" fill="#E5B80B" />
        <text
          x="180"
          y="58"
          textAnchor="middle"
          fill="#0c1f1b"
          fontSize="10"
          fontFamily="Onest, sans-serif"
        >
          Market
        </text>

        <circle
          cx="92"
          cy="168"
          r="18"
          fill="#ffffff"
          stroke="#0c1f1b"
          strokeWidth="2"
        />
        <text
          x="92"
          y="198"
          textAnchor="middle"
          fill="#0c1f1b"
          fontSize="10"
          fontFamily="Onest, sans-serif"
        >
          Verify
        </text>

        <circle
          cx="268"
          cy="168"
          r="18"
          fill="#ffffff"
          stroke="#E5B80B"
          strokeWidth="2"
        />
        <text
          x="268"
          y="198"
          textAnchor="middle"
          fill="#0c1f1b"
          fontSize="10"
          fontFamily="Onest, sans-serif"
        >
          Collect
        </text>
      </svg>

      <p className="text-sm leading-relaxed text-navy/50">
        Consumers, institutions and technology — connected through one
        integrated stack.
      </p>
    </div>
  );
}

export default Hero;
